import { LTLLoadItem } from '@store/ltl/types';
import {
  LoadItemBreakdown,
  LtlQuoteLoadItemViewModel,
} from '@store/orders/types';
import { toDimensionValue } from '@utils/ltl/dimensions';
import { getActiveBreakdowns } from '@utils/ltl/loadItemBreakdown';
import { nestLoadItems } from '@utils/ltl/quoteLoadItems';

export type ItemDifference = {
  fieldName: string;
  original: string | number | boolean;
  current: string | number | boolean;
  index?: number;
  subIndex?: number;
};

export type ValidationResult = {
  isRequoteNeeded: boolean;
  differences: ItemDifference[];
};

export type RequoteCompareOptions = {
  // Also compare mixed-commodity sub-items (loadItemBreakdowns) with the quote.
  compareBreakdowns?: boolean;
};

type LoadItemWithBreakdowns = LTLLoadItem & {
  loadItemBreakdowns?: LoadItemBreakdown[] | null;
};

const addAddressDifference = (
  differences: ItemDifference[],
  fieldName: string,
  current: string,
  original: string,
) => {
  if (current !== original) {
    differences.push({
      fieldName,
      original,
      current,
    });
  }
};

// Helper function to check if an item has hazmat status
const hasHazmat = (item: LTLLoadItem | LtlQuoteLoadItemViewModel): boolean => {
  if ('hazmat' in item && item.hazmat) {
    return true;
  }
  if ('quoteHazmatDetail' in item && item.quoteHazmatDetail) {
    return true;
  }
  return false;
};

const toNumberOrZero = (value: unknown) => {
  const numeric = Number(value ?? 0);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const normalizeClass = (value: unknown) => {
  const text = String(value ?? '');
  const numeric = parseFloat(text);
  return Number.isNaN(numeric) ? text : String(numeric);
};

const getBreakdownDifferences = (
  formItem: LoadItemWithBreakdowns,
  quoteItem: LtlQuoteLoadItemViewModel,
  index: number,
): ItemDifference[] => {
  // Loads booked before sub-items were carried onto the load have no
  // breakdown collection at all (null/undefined) even when the quote does;
  // that is not a change the user made. A present collection is always
  // compared, including an empty one: unsaved quoted sub-items (id 0) are
  // removed outright and saved ones are soft-deleted, and either way the user
  // removed every quoted sub-item, so the active count must be checked.
  if (formItem.loadItemBreakdowns == null) return [];

  const formBreakdowns = getActiveBreakdowns(formItem.loadItemBreakdowns);
  const quoteBreakdowns = quoteItem.quoteLoadItemBreakdown ?? [];

  if (formBreakdowns.length !== quoteBreakdowns.length) {
    return [
      {
        fieldName: 'breakdownCount',
        original: quoteBreakdowns.length,
        current: formBreakdowns.length,
        index,
      },
    ];
  }

  return formBreakdowns.flatMap((formBreakdown, subIndex) => {
    const quoteBreakdown = quoteBreakdowns[subIndex];
    const comparisons: [
      string,
      string | number | boolean,
      string | number | boolean,
    ][] = [
      [
        'breakdownWeight',
        toNumberOrZero(quoteBreakdown.weight),
        toNumberOrZero(formBreakdown.weight),
      ],
      [
        'breakdownPieces',
        toNumberOrZero(quoteBreakdown.pieces),
        toNumberOrZero(formBreakdown.pieces),
      ],
      [
        'breakdownClass',
        normalizeClass(quoteBreakdown.class),
        normalizeClass(formBreakdown.class),
      ],
      [
        'breakdownHazmat',
        Boolean(quoteBreakdown.hazmat),
        Boolean(formBreakdown.hazmat),
      ],
    ];

    return comparisons
      .filter(([, original, current]) => original !== current)
      .map(([fieldName, original, current]) => ({
        fieldName,
        original,
        current,
        index,
        subIndex,
      }));
  });
};

export const areLoadItemsDifferent = (
  formLoadItems: LTLLoadItem[],
  rawQuoteLoadItems: LtlQuoteLoadItemViewModel[],
  options?: RequoteCompareOptions,
): ValidationResult => {
  const differences: ItemDifference[] = [];
  // The quote API may return sub-items as top-level rows linked by
  // parentQuoteLoadItemId. When sub-items are in play, nest them first so
  // they are neither counted as parents nor missed by the breakdown
  // comparison. Without the mixed-commodity option the quote items are used
  // exactly as before.
  const quoteLoadItems =
    options?.compareBreakdowns === true
      ? nestLoadItems(rawQuoteLoadItems)
      : rawQuoteLoadItems;
  // If the count of items is different, we need a requote
  const activeItemsCount = formLoadItems.filter(
    (item) => item.operationType !== 'Delete',
  );
  if (activeItemsCount.length !== quoteLoadItems.length) {
    differences.push({
      fieldName: 'itemCount',
      original: quoteLoadItems.length,
      current: activeItemsCount.length,
    });
    return { isRequoteNeeded: true, differences };
  }

  // Compare each load item
  for (let i = 0; i < formLoadItems.length; i++) {
    const formItem = formLoadItems[i];
    const quoteItem = quoteLoadItems[i];

    // Compare class
    if (formItem.class !== quoteItem.class) {
      differences.push({
        fieldName: 'class',
        original: quoteItem.class ?? '',
        current: formItem.class ?? '',
        index: i,
      });
    }

    // Compare weight
    if (formItem.weight !== quoteItem.weight) {
      differences.push({
        fieldName: 'weight',
        original: quoteItem.weight ?? 0,
        current: formItem.weight ?? 0,
        index: i,
      });
    }

    // Compare dimensions
    if (
      toDimensionValue(formItem.lengthInch) !==
      toDimensionValue(quoteItem.length)
    ) {
      differences.push({
        fieldName: 'length',
        original: toDimensionValue(quoteItem.length) ?? 0,
        current: toDimensionValue(formItem.lengthInch) ?? 0,
        index: i,
      });
    }

    if (
      toDimensionValue(formItem.widthInch) !== toDimensionValue(quoteItem.width)
    ) {
      differences.push({
        fieldName: 'width',
        original: toDimensionValue(quoteItem.width) ?? 0,
        current: toDimensionValue(formItem.widthInch) ?? 0,
        index: i,
      });
    }

    if (
      toDimensionValue(formItem.heightInch) !==
      toDimensionValue(quoteItem.height)
    ) {
      differences.push({
        fieldName: 'height',
        original: toDimensionValue(quoteItem.height) ?? 0,
        current: toDimensionValue(formItem.heightInch) ?? 0,
        index: i,
      });
    }

    // Compare quantity
    if (formItem.quantity !== quoteItem.quantity) {
      differences.push({
        fieldName: 'quantity',
        original: quoteItem.quantity || 0,
        current: formItem.quantity ?? 0,
        index: i,
      });
    }

    // Compare hazmat status
    if (hasHazmat(formItem) !== hasHazmat(quoteItem)) {
      differences.push({
        fieldName: 'hazmat',
        original: hasHazmat(quoteItem),
        current: hasHazmat(formItem),
        index: i,
      });
    }

    if (options?.compareBreakdowns === true) {
      differences.push(...getBreakdownDifferences(formItem, quoteItem, i));
    }
  }

  return { isRequoteNeeded: differences.length > 0, differences };
};

export const areAddressesDifferent = (
  formOrigin: {
    country?: string | null;
    state?: string | null;
    zip?: string | null;
  },
  formDestination: {
    country?: string | null;
    state?: string | null;
    zip?: string | null;
  },
  quoteOrigin: {
    originCountry: string;
    originState: string;
    originZip: string;
  },
  quoteDestination: {
    destinationCountry: string;
    destinationState: string;
    destinationZip: string;
  },
): ValidationResult => {
  const differences: ItemDifference[] = [];
  const normalizeString = (str: string | null | undefined): string => {
    if (str === null || str === undefined) return '';
    return str;
  };

  // Compare origin
  const normalizedOriginCountry = normalizeString(formOrigin.country);
  const normalizedOriginState = normalizeString(formOrigin.state);
  const normalizedOriginZip = normalizeString(formOrigin.zip);
  addAddressDifference(
    differences,
    'originCountry',
    normalizedOriginCountry,
    quoteOrigin.originCountry,
  );
  addAddressDifference(
    differences,
    'originState',
    normalizedOriginState,
    quoteOrigin.originState,
  );
  addAddressDifference(
    differences,
    'originZip',
    normalizedOriginZip,
    quoteOrigin.originZip,
  );

  // Compare destination
  const normalizedDestCountry = normalizeString(formDestination.country);
  const normalizedDestState = normalizeString(formDestination.state);
  const normalizedDestZip = normalizeString(formDestination.zip);
  addAddressDifference(
    differences,
    'destinationCountry',
    normalizedDestCountry,
    quoteDestination.destinationCountry,
  );
  addAddressDifference(
    differences,
    'destinationState',
    normalizedDestState,
    quoteDestination.destinationState,
  );
  addAddressDifference(
    differences,
    'destinationZip',
    normalizedDestZip,
    quoteDestination.destinationZip,
  );

  return {
    isRequoteNeeded: differences.length > 0,
    differences,
  };
};

export const isRequoteNeeded = (
  loadData: {
    loadItems?: LTLLoadItem[];
    originCountry?: string | null;
    originState?: string | null;
    originZip?: string | null;
    destinationCountry?: string | null;
    destinationState?: string | null;
    destinationZip?: string | null;
  },
  quoteParameters: {
    loadItems?: LtlQuoteLoadItemViewModel[];
    originCountry: string;
    originState: string;
    originZip: string;
    destinationCountry: string;
    destinationState: string;
    destinationZip: string;
  },
  options?: RequoteCompareOptions,
): ValidationResult => {
  const allDifferences: ItemDifference[] = [];
  // Check load items differences
  if (loadData.loadItems && quoteParameters.loadItems) {
    const loadItemsResult = areLoadItemsDifferent(
      loadData.loadItems,
      quoteParameters.loadItems,
      options,
    );
    allDifferences.push(...loadItemsResult.differences);
  }

  // Check address differences
  const addressResult = areAddressesDifferent(
    {
      country: loadData.originCountry,
      state: loadData.originState,
      zip: loadData.originZip,
    },
    {
      country: loadData.destinationCountry,
      state: loadData.destinationState,
      zip: loadData.destinationZip,
    },
    {
      originCountry: quoteParameters.originCountry,
      originState: quoteParameters.originState,
      originZip: quoteParameters.originZip,
    },
    {
      destinationCountry: quoteParameters.destinationCountry,
      destinationState: quoteParameters.destinationState,
      destinationZip: quoteParameters.destinationZip,
    },
  );

  allDifferences.push(...addressResult.differences);

  return {
    isRequoteNeeded: allDifferences.length > 0,
    differences: allDifferences,
  };
};
